import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Youtube, Linkedin, Mail, Phone, MapPin, Clock } from "lucide-react";
import { API } from "../App";

const Footer = () => {
  const [contact, setContact] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await fetch(`${API}/settings/public`);
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled) setContact(data.contact || {});
      } catch (e) {
        // ignore
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!contact) return null;

  const social = contact.social || {};
  const legal = contact.legal_links || {};
  const hasAnySocial = social.instagram || social.facebook || social.tiktok || social.youtube || social.linkedin;
  const addressLine = [
    contact.address_street,
    contact.address_number && `nº ${contact.address_number}`,
    contact.address_neighborhood,
    contact.address_city && contact.address_state && `${contact.address_city}/${contact.address_state}`,
    contact.address_cep,
  ].filter(Boolean).join(", ");

  return (
    <footer className="bg-gray-900 text-gray-200 mt-16" data-testid="site-footer">
      <div className="container mx-auto px-6 py-12">
        <div className="grid md:grid-cols-4 gap-8">
          {/* Brand */}
          <div>
            <h3 className="font-heading text-2xl font-bold text-white mb-3">
              {contact.company_name || "GoFood"}
            </h3>
            {contact.tagline && (
              <p className="text-sm text-gray-400 leading-relaxed mb-4">{contact.tagline}</p>
            )}
            {contact.cnpj && (
              <p className="text-xs text-gray-500">CNPJ: {contact.cnpj}</p>
            )}
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-white mb-3">Contato</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              {contact.email && (
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4 shrink-0" />
                  <a href={`mailto:${contact.email}`} className="hover:text-white">{contact.email}</a>
                </li>
              )}
              {contact.whatsapp && (
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4 shrink-0" />
                  <a href={`https://wa.me/${contact.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="hover:text-white">
                    WhatsApp: {contact.whatsapp}
                  </a>
                </li>
              )}
              {contact.phone && !contact.whatsapp && (
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4 shrink-0" />
                  <span>{contact.phone}</span>
                </li>
              )}
              {contact.hours && (
                <li className="flex items-center gap-2">
                  <Clock className="w-4 h-4 shrink-0" />
                  <span>{contact.hours}</span>
                </li>
              )}
              {addressLine && (
                <li className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{addressLine}</span>
                </li>
              )}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-semibold text-white mb-3">Institucional</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              {legal.terms && <li><Link to={legal.terms} className="hover:text-white" data-testid="footer-link-terms">Termos de Uso</Link></li>}
              {legal.privacy && <li><Link to={legal.privacy} className="hover:text-white" data-testid="footer-link-privacy">Política de Privacidade</Link></li>}
              {legal.cookies && <li><Link to={legal.cookies} className="hover:text-white" data-testid="footer-link-cookies">Política de Cookies</Link></li>}
              {legal.faq && <li><Link to={legal.faq} className="hover:text-white" data-testid="footer-link-faq">Perguntas Frequentes</Link></li>}
            </ul>
          </div>

          {/* Social */}
          <div>
            <h4 className="font-semibold text-white mb-3">Redes Sociais</h4>
            {hasAnySocial ? (
              <div className="flex gap-3 flex-wrap">
                {social.instagram && (
                  <a href={social.instagram} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-gray-800 hover:bg-primary flex items-center justify-center transition-colors" aria-label="Instagram">
                    <Instagram className="w-4 h-4" />
                  </a>
                )}
                {social.facebook && (
                  <a href={social.facebook} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-gray-800 hover:bg-primary flex items-center justify-center transition-colors" aria-label="Facebook">
                    <Facebook className="w-4 h-4" />
                  </a>
                )}
                {social.youtube && (
                  <a href={social.youtube} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-gray-800 hover:bg-primary flex items-center justify-center transition-colors" aria-label="YouTube">
                    <Youtube className="w-4 h-4" />
                  </a>
                )}
                {social.linkedin && (
                  <a href={social.linkedin} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-gray-800 hover:bg-primary flex items-center justify-center transition-colors" aria-label="LinkedIn">
                    <Linkedin className="w-4 h-4" />
                  </a>
                )}
                {social.tiktok && (
                  <a href={social.tiktok} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-gray-800 hover:bg-primary flex items-center justify-center transition-colors text-xs font-bold" aria-label="TikTok">
                    TT
                  </a>
                )}
              </div>
            ) : (
              <p className="text-xs text-gray-500">Em breve.</p>
            )}
          </div>
        </div>

        <div className="border-t border-gray-800 mt-10 pt-6 text-center text-xs text-gray-500">
          {contact.copyright || "© 2026 GoFood. Todos os direitos reservados."}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
